import { Menu, MenuItem, Stack, SxProps, Theme } from '@mui/material'
import React from 'react'
import { Typography } from '../Typography'
import { Variant } from '@mui/material/styles/createTypography'
import { J1RightarrowD } from '@e3dc-react/icons'

export interface Breadcrumb {
  title: string
  className?: string
  onClick?: () => void
  children?: Breadcrumb[]
  hidden?: boolean
}

interface Props {
  breadcrumbs: Breadcrumb[]
  titleClassName?: string
  rootClassName?: string
  iconStyle?: SxProps<Theme>
  wrapperRef?: React.RefObject<HTMLDivElement>
  variant?: Variant
  lastItemVariant?: Variant
  breakLastBreadcrumb?: boolean
}

const Breadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  titleClassName,
  rootClassName,
  iconStyle = undefined,
  wrapperRef,
  variant = 'body2',
  lastItemVariant,
  breakLastBreadcrumb = false,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchorEl)

  const onMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget)
  }

  return (
    <Stack direction={'row'} spacing={1} className={rootClassName} width={'100%'} ref={wrapperRef} alignItems={'center'}>
      {breadcrumbs.map((breadcrumb, index) => {
        const isLastBreadcrumb = index === breadcrumbs.length - 1

        const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
          if (breadcrumb.children) {
            onMenuOpen(event)
            return
          }

          breadcrumb.onClick?.()
        }

        return (
          <React.Fragment key={breadcrumb.title + index}>
            {!breadcrumb.hidden && (
              <Typography
                variant={isLastBreadcrumb ? lastItemVariant ?? variant : variant}
                onClick={handleClick}
                sx={theme => ({
                  color: breadcrumb?.onClick ? theme.palette.primary.main : theme.palette.text.primary,
                  cursor: breadcrumb?.onClick ? 'pointer' : 'default',
                })}
                className={breadcrumb.className ?? titleClassName}
              >
                {breadcrumb.title}
              </Typography>
            )}
            {(index < breadcrumbs.length - 1 || breakLastBreadcrumb) && <J1RightarrowD sx={iconStyle} />}
            {breadcrumb.children && (
              <Menu
                open={menuOpen}
                anchorEl={menuAnchorEl}
                autoFocus={false}
                onClose={() => setMenuAnchorEl(null)}
                sx={{ '& .MuiList-root': { backgroundColor: 'background.paper' } }}
              >
                {breadcrumb.children.map((child, childIndex) => (
                  <MenuItem key={child.title + childIndex} onClick={child.onClick}>
                    <Typography>{child.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </React.Fragment>
        )
      })}
    </Stack>
  )
}

export default Breadcrumbs
